import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';

import {
  Form,
  Button,
  TextInput,
  SelectInput,
  ButtonsWrapper,
} from '@thinknet-gmbh/platform-common-ui';
import { PLATFORM_RULES } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useCreatePermissionsGroup } from 'src/hooks/permission-group/create-permission.hook';

import { RulesListSelect } from './components/';
import { IPermission, IPermissionDoc } from './modal-form.interfaces';
import { DEFAULT_PERMISSION_DOC, YUU_HR_PERMISSIONS } from './modal-form.constants';

const ModalForm = ({ closeModal }: { closeModal: () => void }) => {
  const [formData, setFormData] = useState<IPermissionDoc>(DEFAULT_PERMISSION_DOC);
  const { t } = useTranslation(['common', 'settings']);
  const [form] = Form.useForm();

  const resetFields = () => {
    setFormData(DEFAULT_PERMISSION_DOC);
    form.resetFields();
    closeModal();
  };

  const currentCompanyId = useGetCurrentCompanyId();
  const createPermissionGroup = useCreatePermissionsGroup({ onSuccess: resetFields });

  const onPermissionNameInput = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((oldValue: IPermissionDoc) => ({ ...oldValue, name: event.target.value }));
  };
  const onPermissionSelect = (value?: string) => {
    const isIncluded = formData.permissions.find(item => item.permission === value);

    if (value && !isIncluded) {
      const newPermission: IPermission = {
        permission: value,
        isConfirmed: false,
        rule: PLATFORM_RULES.manageOwn,
      };

      setFormData(({ name, permissions }: IPermissionDoc) => ({
        name,
        permissions: [...permissions, newPermission],
      }));
    }
  };

  const onRuleChange = (index: number) => (newRule: string) =>
    setFormData((prevData: IPermissionDoc) => {
      const newData = { ...prevData };

      newData.permissions[index].rule = newRule;
      newData.permissions[index].isConfirmed = true;

      return newData;
    });
  const onPermissionRemove = (permission: string) => () =>
    setFormData(({ name, permissions }: IPermissionDoc) => ({
      name,
      permissions: permissions.filter(data => data.permission !== permission),
    }));

  const onFinish = () => {
    createPermissionGroup({
      variables: {
        payload: {
          companyId: currentCompanyId,
          name: formData.name,
          permissions: formData.permissions.map(({ permission, rule }) => ({ permission, rule })),
        },
      },
    });
  };

  useEffect(() => {
    if (!formData.permissions.length) {
      form.resetFields(['permission']);
    }
  }, [formData.permissions, form]);

  return (
    <Form layout="vertical" form={form} onFinish={onFinish}>
      <Form.Item name="title" label={t('settings:permissionGroupSettings.labels.title')}>
        <TextInput
          placeholder={t('settings:permissionGroupSettings.placeholders.administrator')}
          onChange={onPermissionNameInput}
          maxLength={50}
        />
      </Form.Item>
      <Form.Item
        name="permission"
        label={t('settings:permissionGroupSettings.labels.availablePages')}
        rules={[{ required: true }]}
      >
        <SelectInput
          placeholder={t('settings:permissionGroupSettings.placeholders.selectNeededPage')}
          onChange={onPermissionSelect}
        >
          {YUU_HR_PERMISSIONS.map(permission => (
            <SelectInput.Option
              key={permission}
              value={permission}
              disabled={!!formData.permissions.find(item => item.permission === permission)}
            >
              {t(`settings:permissionGroupSettings.yuuHrPermissions.${permission}`)}
            </SelectInput.Option>
          ))}
        </SelectInput>
      </Form.Item>
      {formData.permissions.map(({ permission, rule }, index) => (
        <RulesListSelect
          key={permission}
          selectedRule={rule}
          isViewSeparator={index !== formData.permissions.length - 1}
          onRuleChange={onRuleChange(index)}
          onPermissionRemove={onPermissionRemove(permission)}
          permissionName={t(`settings:permissionGroupSettings.yuuHrPermissions.${permission}`)}
        />
      ))}
      <ButtonsWrapper marginTop={14}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={resetFields}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default ModalForm;
